import * as React from "react";
import moment from "moment";
import UpdatePendingReviewModal from "./UpdatePendingReviewModal";
import InsertPendingReviewModal from "./InsertPendingReviewModal";
import {
  Box,
  Grid,
  Stack,
  Button,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Pagination,
  Select,
  MenuItem,
} from "@mui/material";
import { Link, useHistory } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PublishIcon from "@mui/icons-material/Publish";
import { AuthenticationContext, ImagesContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import LoadingTable from "../shared/LoadingTable";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import { PendingReviewCandidate } from "../types/pendingReview";

// ─── Styles (same as medications.tsx) ────────────────────────────────────────

const subtextStyle: React.CSSProperties = {
  margin: 0,
  color: "var(--med-grey, #4A4442)",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontFamily: "QuicksandMedium",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: 1.2,
};

const darkHeaderStyle: React.CSSProperties = {
  margin: 0,
  color: "#1E1E1E",
  fontFamily: "QuicksandBold",
  fontWeight: 700,
};

const TABLE_MAX_HEIGHT = 420;

// ─── Types ────────────────────────────────────────────────────────────────────

interface PayerSummaryRow {
  payer: { id: number; name: string };
  signed_encounters: number;
  signed_interventions: number;
  supply_requests: number;
}

interface PerformanceMetricRow {
  program: string;
  payer_name: string;
  contract_anniversary_date: string | null;
  total_patients: number;
  plan_eligible_patients: number;
  plan_eligible_pct: number;
  program_eligible_patients: number;
  program_eligible_pct: number;
  enrolled_patients: number;
  enrolled_pct: number;
}

interface PendingReviewRow {
  id: number;
  import_run_id: number | null;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  date_of_birth: string | null;
  member_unique_id: string | null;
  insurance_member_id: string | null;
  insurance_family_id: string | null;
  employer_name: string | null;
  relationship_to_subscriber: string | null;
  gender: string | null;
  phone_number: string | null;
  email: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  zip_code_extension: string | null;
  benefit_ending_date: string | null;
  plan_eligibility_indicator: string | null;
  review_reason: string | null;
  candidates: PendingReviewCandidate[];
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

const lastMonthStart = () => moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
const lastMonthEnd = () => moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");

// ─── Component ────────────────────────────────────────────────────────────────

const HealthNavigatorSummaryReport: React.FC = () => {
  const authCtx = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);

  // Billing Summary — default to last full calendar month
  const [startDate, setStartDate] = React.useState<string>(lastMonthStart());
  const [endDate, setEndDate] = React.useState<string>(lastMonthEnd());
  const [billingData, setBillingData] = React.useState<PayerSummaryRow[]>([]);
  const [billingLoading, setBillingLoading] = React.useState<boolean>(false);
  const [billingError, setBillingError] = React.useState<string | null>(null);

  // Performance Metrics
  const [metricsData, setMetricsData] = React.useState<PerformanceMetricRow[]>([]);
  const [metricsLoading, setMetricsLoading] = React.useState<boolean>(true);
  const [metricsError, setMetricsError] = React.useState<string | null>(null);
  const [metricsSort, setMetricsSort] = React.useState<{ field: string; direction: "ascending" | "descending" }>({
    field: "program",
    direction: "ascending",
  });

  // Pending Reviews
  const [pendingData, setPendingData] = React.useState<PendingReviewRow[]>([]);
  const [pendingLoading, setPendingLoading] = React.useState<boolean>(true);
  const [pendingError, setPendingError] = React.useState<string | null>(null);
  const [pendingPage, setPendingPage] = React.useState<number>(1);
  const [pendingTotal, setPendingTotal] = React.useState<number>(0);
  const [perPage, setPerPage] = React.useState<number>(20);

  // Update modal
  const [updateReviewModal, setUpdateReviewModal] = React.useState<{
    open: boolean;
    reviewId: number | null;
    candidates: PendingReviewCandidate[];
  }>({ open: false, reviewId: null, candidates: [] });

  const [insertReviewModal, setInsertReviewModal] = React.useState<{
    open: boolean;
    reviewId: number | null;
  }>({ open: false, reviewId: null });

  // ── Date validation ──────────────────────────────────────────────────────────

  const dateRangeError = React.useMemo<string | null>(() => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      return "Start date cannot be later than end date.";
    }
    return null;
  }, [startDate, endDate]);

  const isValidDateSelection = React.useMemo<boolean>(() => {
    if (dateRangeError) return false;
    if ((startDate && !endDate) || (!startDate && endDate)) return false;
    return true;
  }, [startDate, endDate, dateRangeError]);

  // ── Fetches ──────────────────────────────────────────────────────────────────

  const fetchBillingData = React.useCallback(() => {
    setBillingLoading(true);
    setBillingError(null);
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    fetch(`/data_fetching/billing_summary?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(authCtx.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          setBillingData(result.resource || []);
        } else {
          setBillingError(result.error || "Failed to load billing summary.");
        }
      })
      .catch(() => setBillingError("An error occurred while loading the billing summary."))
      .finally(() => setBillingLoading(false));
  }, [startDate, endDate, authCtx.csrfToken]);

  React.useEffect(() => {
    if (isValidDateSelection) fetchBillingData();
  }, [fetchBillingData, isValidDateSelection]);

  React.useEffect(() => {
    fetch("/data_fetching/performance_metrics", {
      method: "GET",
      headers: getHeaders(authCtx.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          setMetricsData(result.resource || []);
        } else {
          setMetricsError(result.error || "Failed to load performance metrics.");
        }
      })
      .catch(() => setMetricsError("An error occurred while loading performance metrics."))
      .finally(() => setMetricsLoading(false));
  }, []);

  const fetchPendingData = React.useCallback(() => {
    setPendingLoading(true);
    setPendingError(null);
    fetch(`/data_fetching/pending_reviews?page=${pendingPage}&per_page=${perPage}`, {
      method: "GET",
      headers: getHeaders(authCtx.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          setPendingData(result.resource || []);
          setPendingTotal(result.meta?.total || 0);
        } else {
          setPendingError(result.error || "Failed to load pending reviews.");
        }
      })
      .catch(() => setPendingError("An error occurred while loading pending reviews."))
      .finally(() => setPendingLoading(false));
  }, [pendingPage, perPage, authCtx.csrfToken]);

  React.useEffect(() => {
    fetchPendingData();
  }, [fetchPendingData]);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const buildViewUrl = (path: string, payerId: number, payerName: string): string => {
    const p = new URLSearchParams({
      payer_id: String(payerId),
      payer_name: payerName,
      start_date: startDate,
      end_date: endDate,
    });
    return `${path}?${p.toString()}`;
  };

  const buildSupplyRequestsViewUrl = (payerId: number, payerName: string): string => {
    const p = new URLSearchParams({
      payer_id: String(payerId),
      payer_name: payerName,
      start_date: startDate,
      end_date: endDate,
    });
    // Pre-filter to completed so the detail count matches the billing summary count
    p.append("statuses[]", "completed");
    return `/supply-requests-billing-report?${p.toString()}`;
  };

  const getSortIcon = (field: string) => {
    if (metricsSort.field !== field) {
      return <img src={images?.sort_plain_src} width="10" className="sort-icon" alt="Sort" />;
    }
    return metricsSort.direction === "ascending" ? (
      <img src={images?.sort_ascending_src} width="10" className="sort-icon" alt="Sort Asc" />
    ) : (
      <img src={images?.sort_descending_src} width="10" className="sort-icon" alt="Sort Desc" />
    );
  };

  const toggleSort = (field: string) => {
    setMetricsSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "ascending" ? "descending" : "ascending",
    }));
  };

  const sortedMetrics = React.useMemo(() => {
    const list = [...metricsData];
    list.sort((a, b) => {
      const aVal = String(a[metricsSort.field as keyof PerformanceMetricRow] ?? "").toLowerCase();
      const bVal = String(b[metricsSort.field as keyof PerformanceMetricRow] ?? "").toLowerCase();
      return aVal > bVal ? 1 : -1;
    });
    return metricsSort.direction === "descending" ? list.reverse() : list;
  }, [metricsData, metricsSort]);

  const totalPendingPages = Math.ceil(pendingTotal / perPage);

  const onInsertClick = (row: PendingReviewRow) => {
    setInsertReviewModal({ open: true, reviewId: row.id });
  };

  const handleCloseInsertReviewModal = () => {
    setInsertReviewModal({ open: false, reviewId: null });
  };

  const handleInsertReviewSuccess = (reviewId: number) => {
    setPendingData((prev) => prev.filter((r) => r.id !== reviewId));
    setPendingTotal((prev) => Math.max(0, prev - 1));
  };

  const onUpdateClick = (row: PendingReviewRow) => {
    setUpdateReviewModal({ open: true, reviewId: row.id, candidates: row.candidates || [] });
  };

  const handleCloseUpdateReviewModal = () => {
    setUpdateReviewModal({ open: false, reviewId: null, candidates: [] });
  };

  const handleUpdateReviewSuccess = (reviewId: number) => {
    setPendingData((prev) => prev.filter((r) => r.id !== reviewId));
    setPendingTotal((prev) => Math.max(0, prev - 1));
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box className="report-main-content" sx={{ paddingLeft: "120px" }}>
      {/* ── Page header (same pattern as ONE Program Report) ─────────────────── */}
      <Box className="main-header">
        <Box className="heading-with-back-button-section">
          <Stack direction="row" columnGap="12px">
            <Link
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
              to="/reports"
            >
              <ArrowBackIosNewIcon
                style={{
                  borderRadius: "3px",
                  padding: "8px",
                  color: "black",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  fontSize: "14px",
                }}
              />
            </Link>
            <Typography
              sx={{
                fontSize: "24px",
                color: "black",
                fontWeight: 700,
                lineHeight: "normal",
              }}
            >
              My Health Navigator Summary Report
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* ── Billing Summary ───────────────────────────────────────────────────── */}
      <Grid container className="panel-container" id="billing-summary">
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    paddingX={3}
                    paddingY={2.3}
                    alignItems="flex-start"
                    display="flex"
                  >
                    <Stack spacing={0.5}>
                      <h3 style={{ margin: 0 }}>Billing Summary</h3>
                      <p style={subtextStyle}>
                        Review billing activity by payer for the selected reporting period.
                      </p>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ alignSelf: "center" }}>
                      <DatePickerWithLabel
                        date={startDate}
                        setDate={setStartDate}
                        label="Report Period Start Date"
                        initialDate={lastMonthStart()}
                        width="185px"
                      />
                      <DatePickerWithLabel
                        date={endDate}
                        setDate={setEndDate}
                        label="Report Period End Date"
                        initialDate={lastMonthEnd()}
                        width="185px"
                      />
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>

              {dateRangeError && (
                <Box sx={{ px: 3, pb: 1 }}>
                  <Alert severity="warning">{dateRangeError}</Alert>
                </Box>
              )}

              <Grid
                container
                className="medication-container grey-container"
                style={{ marginTop: "-11px" }}
              >
                <Grid item xs={12} className="medication-table-container" sx={{ mt: 1 }}>
                  {billingLoading ? (
                    <LoadingTable />
                  ) : billingError ? (
                    <Box sx={{ p: 2 }}>
                      <Alert severity="error">{billingError}</Alert>
                    </Box>
                  ) : (
                    <TableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
                      <Table stickyHeader>
                        <TableHead className="table-header-box">
                          <TableRow>
                            <TableCell>
                              <p className="table-header" style={darkHeaderStyle}>Payer</p>
                            </TableCell>
                            <TableCell>
                              <p className="table-header" style={darkHeaderStyle}>Signed Encounters</p>
                            </TableCell>
                            <TableCell>
                              <p className="table-header" style={darkHeaderStyle}>Signed Interventions</p>
                            </TableCell>
                            <TableCell>
                              <p className="table-header" style={darkHeaderStyle}>Supply Requests</p>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {billingData.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                No payer data found for the selected period.
                              </TableCell>
                            </TableRow>
                          ) : (
                            billingData.map((row) => (
                              <TableRow key={row.payer.id}>
                                <TableCell>{row.payer.name}</TableCell>
                                <TableCell>
                                  <Box>
                                    <div style={{ fontSize: "22px", fontWeight: 500 }}>
                                      {row.signed_encounters.toLocaleString()}
                                    </div>
                                    <Link
                                      to={buildViewUrl("/signed-encounters-billing-report", row.payer.id, row.payer.name)}
                                      style={{ fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "3px", color: "#1976d2" }}
                                    >
                                      View Report <OpenInNewIcon sx={{ fontSize: 13 }} />
                                    </Link>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <div style={{ fontSize: "22px", fontWeight: 500 }}>
                                      {row.signed_interventions.toLocaleString()}
                                    </div>
                                    <Link
                                      to={buildViewUrl("/signed-interventions-billing-report", row.payer.id, row.payer.name)}
                                      style={{ fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "3px", color: "#1976d2" }}
                                    >
                                      View Report <OpenInNewIcon sx={{ fontSize: 13 }} />
                                    </Link>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <div style={{ fontSize: "22px", fontWeight: 500 }}>
                                      {row.supply_requests.toLocaleString()}
                                    </div>
                                    <Link
                                      to={buildSupplyRequestsViewUrl(row.payer.id, row.payer.name)}
                                      style={{ fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "3px", color: "#1976d2" }}
                                    >
                                      View Report <OpenInNewIcon sx={{ fontSize: 13 }} />
                                    </Link>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* ── Live Performance Metrics ──────────────────────────────────────────── */}
      <Grid container className="panel-container" sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    paddingX={3}
                    paddingY={2.3}
                    alignItems="flex-start"
                    display="flex"
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <h3 style={{ margin: 0 }}>Live Performance Metrics</h3>
                        <Tooltip title="Live Performance Metrics reflect the current data available in Starfield and do not represent a historical snapshot. Starfield does not currently maintain historical member-file snapshots and users may export the table periodically to maintain their own historical records.">
                          <InfoOutlinedIcon sx={{ fontSize: 18, color: "#888", cursor: "pointer" }} />
                        </Tooltip>
                      </Stack>
                      <p style={subtextStyle}>
                        Real-time enrollment and eligibility data across all payer programs.
                      </p>
                    </Stack>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        alignSelf: "center",
                        backgroundColor: "#E87722",
                        "&:hover": { backgroundColor: "#c9641a" },
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                      }}
                      onClick={() => window.open("/data_fetching/performance_metrics/export_csv", "_blank")}
                    >
                      Export as CSV
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              <Grid
                container
                className="medication-container grey-container"
                style={{ marginTop: "-11px" }}
              >
                <Grid item xs={12} className="medication-table-container" sx={{ mt: 1 }}>
                  {metricsLoading ? (
                    <LoadingTable />
                  ) : metricsError ? (
                    <Box sx={{ p: 2 }}>
                      <Alert severity="error">{metricsError}</Alert>
                    </Box>
                  ) : (
                    <TableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
                      <Table stickyHeader>
                        <TableHead className="table-header-box">
                          <TableRow>
                            <TableCell
                              className="nowrap-header"
                              style={{ cursor: "pointer" }}
                              onClick={() => toggleSort("program")}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <p className="table-header" style={darkHeaderStyle}>Program</p>
                                <span className="sortIndicator">{getSortIcon("program")}</span>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <p className="table-header" style={darkHeaderStyle}>Contract Anniversary Date</p>
                            </TableCell>
                            <TableCell align="center">
                              <p className="table-header" style={darkHeaderStyle}>Total Patients</p>
                            </TableCell>
                            <TableCell align="center">
                              <p className="table-header" style={darkHeaderStyle}>Plan Eligible Patients</p>
                            </TableCell>
                            <TableCell align="center">
                              <p className="table-header" style={darkHeaderStyle}>Program Eligible Patients</p>
                            </TableCell>
                            <TableCell align="center">
                              <p className="table-header" style={darkHeaderStyle}>Enrolled Patients</p>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedMetrics.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                No program data available.
                              </TableCell>
                            </TableRow>
                          ) : (
                            sortedMetrics.map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.program}</TableCell>
                                <TableCell align="center">{row.contract_anniversary_date || "Not Configured"}</TableCell>
                                <TableCell align="center">{row.total_patients.toLocaleString()}</TableCell>
                                <TableCell align="center">
                                  {`${row.plan_eligible_patients.toLocaleString()} - ${row.plan_eligible_pct}%`}
                                </TableCell>
                                <TableCell align="center">
                                  {`${row.program_eligible_patients.toLocaleString()} - ${row.program_eligible_pct}%`}
                                </TableCell>
                                <TableCell align="center">
                                  {`${row.enrolled_patients.toLocaleString()} - ${row.enrolled_pct}%`}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* ── Live Pending Reviews ──────────────────────────────────────────────── */}
      <Grid container className="panel-container" sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    paddingX={3}
                    paddingY={2.3}
                    alignItems="flex-start"
                    display="flex"
                  >
                    <Stack spacing={0.5}>
                      <h3 style={{ margin: 0 }}>Live Pending Reviews</h3>
                      <p style={subtextStyle}>
                        Patients assigned to a payer program pending eligibility review.
                      </p>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>

              <Grid
                container
                className="medication-container grey-container"
                style={{ marginTop: "-11px" }}
              >
                <Grid item xs={12} className="medication-table-container" sx={{ mt: 1 }}>
                  {pendingLoading ? (
                    <LoadingTable />
                  ) : pendingError ? (
                    <Box sx={{ p: 2 }}>
                      <Alert severity="error">{pendingError}</Alert>
                    </Box>
                  ) : (
                    <>
                      <TableContainer className="report-table-container" sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
                        <Table stickyHeader>
                          <TableHead className="table-header-box">
                            <TableRow>
                              <TableCell sx={{ minWidth: "80px" }} />
                              <TableCell sx={{ minWidth: "120px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>First Name</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "120px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Middle Name</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "120px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Last Name</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "120px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Date of Birth</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "150px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Member Unique ID</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "160px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Insurance Member ID</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "160px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Insurance Family ID</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "140px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Employer Name</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "190px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Relationship to Subscriber</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "80px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Gender</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "130px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Phone Number</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "180px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Email</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "150px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Address Line 1</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "150px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Address Line 2</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "100px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>City</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "80px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>State</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "100px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>ZIP Code</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "150px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>ZIP Code Extension</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "160px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Benefit Ending Date</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "190px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Plan Eligibility Indicator</p>
                              </TableCell>
                              <TableCell sx={{ minWidth: "260px", whiteSpace: "nowrap" }}>
                                <p className="table-header" style={darkHeaderStyle}>Review Reason</p>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pendingData.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={22} align="center">No pending reviews found.</TableCell>
                              </TableRow>
                            ) : (
                              pendingData.map((row) => (
                                <TableRow key={row.id}>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Tooltip title="Insert">
                                        <IconButton
                                          size="small"
                                          onClick={() => onInsertClick(row)}
                                          sx={{ color: "#000" }}
                                        >
                                          <PersonAddIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Update">
                                        <IconButton
                                          size="small"
                                          onClick={() => onUpdateClick(row)}
                                          sx={{ color: "#000" }}
                                        >
                                          <PublishIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{row.first_name || "—"}</TableCell>
                                  <TableCell>{row.middle_name || "—"}</TableCell>
                                  <TableCell>{row.last_name || "—"}</TableCell>
                                  <TableCell>{row.date_of_birth || "—"}</TableCell>
                                  <TableCell>{row.member_unique_id || "—"}</TableCell>
                                  <TableCell>{row.insurance_member_id || "—"}</TableCell>
                                  <TableCell>{row.insurance_family_id || "—"}</TableCell>
                                  <TableCell>{row.employer_name || "—"}</TableCell>
                                  <TableCell>{row.relationship_to_subscriber || "—"}</TableCell>
                                  <TableCell>{row.gender || "—"}</TableCell>
                                  <TableCell>{row.phone_number || "—"}</TableCell>
                                  <TableCell>{row.email || "—"}</TableCell>
                                  <TableCell>{row.address_line_1 || "—"}</TableCell>
                                  <TableCell>{row.address_line_2 || "—"}</TableCell>
                                  <TableCell>{row.city || "—"}</TableCell>
                                  <TableCell>{row.state || "—"}</TableCell>
                                  <TableCell>{row.zip_code || "—"}</TableCell>
                                  <TableCell>{row.zip_code_extension || "—"}</TableCell>
                                  <TableCell>{row.benefit_ending_date || "—"}</TableCell>
                                  <TableCell>{row.plan_eligibility_indicator || "—"}</TableCell>
                                  <TableCell>
                                    {row.review_reason ? (
                                      <Tooltip title={row.review_reason}>
                                        <span style={{ display: "inline-block", maxWidth: "240px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {row.review_reason}
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      "—"
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          marginTop: 2,
                          marginBottom: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: "14px" }}>Rows per page</Typography>
                        <Select
                          id="pending-reviews-page-size-select"
                          value={perPage}
                          onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setPendingPage(1);
                          }}
                          size="small"
                          sx={{
                            minWidth: 70,
                            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                            "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                          }}
                        >
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                          <MenuItem value={150}>150</MenuItem>
                        </Select>
                        {totalPendingPages > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Pagination
                              count={totalPendingPages}
                              page={pendingPage}
                              onChange={(_event, page) => setPendingPage(page)}
                              boundaryCount={1}
                              siblingCount={1}
                              showFirstButton
                              showLastButton
                            />
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <UpdatePendingReviewModal
        open={updateReviewModal.open}
        reviewId={updateReviewModal.reviewId}
        candidates={updateReviewModal.candidates}
        onClose={handleCloseUpdateReviewModal}
        onSuccess={handleUpdateReviewSuccess}
      />
      <InsertPendingReviewModal
        open={insertReviewModal.open}
        reviewId={insertReviewModal.reviewId}
        onClose={handleCloseInsertReviewModal}
        onSuccess={handleInsertReviewSuccess}
      />
    </Box>
  );
};

export default HealthNavigatorSummaryReport;
